import React from 'react';
import styled from 'styled-components';
import { useElementSize } from 'usehooks-ts';
import { useTheme } from '../../../theme';
import type { ChartBlock as ChartBlockType } from '../../../types/blocks';
import type { Chart } from '../../../types/charts';
import { validateChart } from '../../../validators/charts';
import AreaChart from './Charts/areaChart';
import BarChart from './Charts/barChart';
import DonutChart from './Charts/donutChart';
import LineChart from './Charts/lineChart';
import MultisetBarChart from './Charts/multisetBarChart';
import PieChart from './Charts/pieChart';
import ScatterChart from './Charts/scatterChart';
import StackedBarChart from './Charts/stackedBarChart';

interface RenderChartProps {
  chartData: Chart;
  width: number;
  height: number;
  chartId?: string;
}

const RenderChart: React.FC<RenderChartProps> = ({ chartData, width, height, chartId }) => {
  switch (chartData.chart_type) {
    case 'bar':
      return <BarChart chartData={chartData} width={width} height={height} chartId={chartId} />;
    case 'line':
      return <LineChart chartData={chartData} width={width} height={height} chartId={chartId} />;
    case 'area':
      return <AreaChart chartData={chartData} width={width} height={height} chartId={chartId} />;
    case 'pie':
      return <PieChart chartData={chartData} width={width} height={height} chartId={chartId} />;
    case 'donut':
      return <DonutChart chartData={chartData} width={width} height={height} chartId={chartId} />;
    case 'scatter':
      return <ScatterChart chartData={chartData} width={width} height={height} chartId={chartId} />;
    case 'stackedBar':
      return (
        <StackedBarChart chartData={chartData} width={width} height={height} chartId={chartId} />
      );
    case 'multisetBar':
      return (
        <MultisetBarChart chartData={chartData} width={width} height={height} chartId={chartId} />
      );
    default:
      return null;
  }
};

interface ChartBlockProps {
  block: ChartBlockType;
  chartId?: string;
}

const ChartBlock: React.FC<ChartBlockProps> = ({ block, chartId }) => {
  const theme = useTheme();
  const [chartContainerRef, { width }] = useElementSize();

  // Validate chart data before rendering
  const validatedChart = validateChart(block);

  // Calculate responsive dimensions
  const chartWidth = Math.max(width - 32, 300); // Account for padding, minimum 300px
  const chartHeight = 350; // Fixed height for consistency

  return (
    <ChartWrapper themeMode={theme.mode} ref={chartContainerRef}>
      <RenderChart
        chartData={validatedChart}
        width={chartWidth}
        height={chartHeight}
        chartId={chartId}
      />
    </ChartWrapper>
  );
};

export default ChartBlock;

const ChartWrapper = styled.div<{ themeMode: string }>`
  margin-top: 12px;
  padding: 8px;
  border-radius: 8px;
  background: ${(props) => (props.themeMode === 'dark' ? '#1D1D1D' : '#fff')};
  box-shadow: ${(props) =>
    props.themeMode === 'dark' ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.05)'};
  width: 100%;
`;
